import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { fetchProfile, saveProfile } from '../services/profileService.js'

const STORAGE_KEY = 'keyzee-affordability'
const ANNUAL_RATE = 0.045

const DEFAULT_PROFILE = {
  income: '',
  partnerIncome: '',
  depositAmount: '',
  termYears: 25,
  isJoint: false,
  buyerType: 'standard',
}

const readStored = () => {
  if (typeof window === 'undefined') return DEFAULT_PROFILE
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored ? { ...DEFAULT_PROFILE, ...JSON.parse(stored) } : DEFAULT_PROFILE
  } catch {
    return DEFAULT_PROFILE
  }
}

const SCOTTISH_PREFIXES = ['AB', 'DD', 'DG', 'EH', 'FK', 'IV', 'KA', 'KW', 'KY', 'ML', 'PA', 'PH', 'TD', 'ZE']
const WELSH_PREFIXES = ['CF', 'LD', 'LL', 'NP', 'SA']

const detectRegion = (postcode) => {
  if (!postcode) return 'england'
  const outcode = postcode.toUpperCase().trim().split(' ')[0].replace(/\d.*$/, '')
  if (SCOTTISH_PREFIXES.some((p) => outcode.startsWith(p)) || outcode === 'G') return 'scotland'
  if (WELSH_PREFIXES.some((p) => outcode.startsWith(p))) return 'wales'
  return 'england'
}

const calcBandedTax = (value, bands) => {
  let remaining = value
  let total = 0
  let lastCap = 0
  for (const band of bands) {
    const taxable = Math.max(0, Math.min(remaining, band.upTo - lastCap))
    total += taxable * band.rate
    remaining -= taxable
    lastCap = band.upTo
    if (remaining <= 0) break
  }
  return total
}

const calcStampDuty = (price, region, buyerType) => {
  if (price <= 0) return 0
  const surcharge = buyerType === 'additional' ? (region === 'england' ? 0.03 : 0.04) : 0

  if (region === 'scotland') {
    const bands = buyerType === 'ftb'
      ? [{ upTo: 175000, rate: 0 }, { upTo: 250000, rate: 0.02 }, { upTo: 325000, rate: 0.05 }, { upTo: 750000, rate: 0.1 }, { upTo: Infinity, rate: 0.12 }]
      : [{ upTo: 145000, rate: 0 }, { upTo: 250000, rate: 0.02 }, { upTo: 325000, rate: 0.05 }, { upTo: 750000, rate: 0.1 }, { upTo: Infinity, rate: 0.12 }]
    return calcBandedTax(price, bands.map((b) => ({ ...b, rate: b.rate + surcharge })))
  }

  if (region === 'wales') {
    const bands = [{ upTo: 225000, rate: 0 }, { upTo: 400000, rate: 0.06 }, { upTo: 750000, rate: 0.075 }, { upTo: 1500000, rate: 0.1 }, { upTo: Infinity, rate: 0.12 }]
    return calcBandedTax(price, bands.map((b) => ({ ...b, rate: b.rate + surcharge })))
  }

  let bands
  if (buyerType === 'ftb' && price <= 625000) {
    bands = [{ upTo: 425000, rate: 0 }, { upTo: 625000, rate: 0.05 }, { upTo: 925000, rate: 0.05 }, { upTo: 1500000, rate: 0.1 }, { upTo: Infinity, rate: 0.12 }]
  } else {
    bands = [{ upTo: 250000, rate: 0 }, { upTo: 925000, rate: 0.05 }, { upTo: 1500000, rate: 0.1 }, { upTo: Infinity, rate: 0.12 }]
  }
  return calcBandedTax(price, bands.map((b) => ({ ...b, rate: b.rate + surcharge })))
}

export const calcMonthlyRepayment = (loanAmount, termYears = 25) => {
  if (loanAmount <= 0 || termYears <= 0) return 0
  const monthlyRate = ANNUAL_RATE / 12
  const totalMonths = termYears * 12
  return (loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -totalMonths))
}

const AffordabilityContext = createContext(null)

export const AffordabilityProvider = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0()
  const [profile, setProfile] = useState(readStored)
  const [isOpen, setIsOpen] = useState(false)
  const dbSyncTimeout = useRef(null)

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user?.sub) return
    getAccessTokenSilently().then((token) =>
      fetchProfile(user.sub, { token })
    ).then((profiles) => {
      if (profiles?.affordability) {
        const { updatedAt: _u, ...dbProfile } = profiles.affordability
        setProfile((prev) => {
          const merged = { ...DEFAULT_PROFILE, ...dbProfile, ...prev }
          try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)) } catch { /* ignore */ }
          return merged
        })
      }
    }).catch(() => { /* ignore — DB unavailable, localStorage is fine */ })
  }, [authLoading, isAuthenticated, user?.sub, getAccessTokenSilently])

  const updateProfile = useCallback((updates) => {
    setProfile((prev) => {
      const next = { ...prev, ...updates }
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
      if (isAuthenticated && user?.sub) {
        clearTimeout(dbSyncTimeout.current)
        dbSyncTimeout.current = setTimeout(() => {
          getAccessTokenSilently().then((token) =>
            saveProfile({ userSub: user.sub, affordabilityProfile: next }, { token })
          ).catch(() => { /* ignore — localStorage is the source of truth */ })
        }, 1000)
      }
      return next
    })
  }, [isAuthenticated, user?.sub, getAccessTokenSilently])

  const toggleSidebar = useCallback(() => setIsOpen((prev) => !prev), [])
  const closeSidebar = useCallback(() => setIsOpen(false), [])

  const getAffordability = useCallback((price, postcode) => {
    const totalIncome = (Number(profile.income) || 0) + (profile.isJoint ? (Number(profile.partnerIncome) || 0) : 0)
    const deposit = Number(profile.depositAmount) || 0
    if (!totalIncome || !price) return null
    const maxBorrowable = totalIncome * 4.5
    const loanRequired = Math.max(0, price - deposit)
    const region = detectRegion(postcode)
    const duty = calcStampDuty(price, region, profile.buyerType)
    const monthly = calcMonthlyRepayment(loanRequired, profile.termYears)
    const monthlyIncome = totalIncome / 12
    let status
    if (loanRequired > maxBorrowable) {
      status = 'over_budget'
    } else if (loanRequired > maxBorrowable * 0.9 || monthly > monthlyIncome * 0.4) {
      status = 'stretching'
    } else {
      status = 'in_budget'
    }
    return { status, monthly, duty, loanRequired, maxBorrowable, deposit, region }
  }, [profile])

  const totalIncome = (Number(profile.income) || 0) + (profile.isJoint ? (Number(profile.partnerIncome) || 0) : 0)
  const hasProfile = totalIncome > 0

  const value = useMemo(() => ({
    profile,
    updateProfile,
    isOpen,
    toggleSidebar,
    closeSidebar,
    getAffordability,
    hasProfile,
    totalIncome,
    annualRate: ANNUAL_RATE,
  }), [profile, updateProfile, isOpen, toggleSidebar, closeSidebar, getAffordability, hasProfile, totalIncome])

  return <AffordabilityContext.Provider value={value}>{children}</AffordabilityContext.Provider>
}

export const useAffordability = () => {
  const ctx = useContext(AffordabilityContext)
  if (!ctx) throw new Error('useAffordability must be used within AffordabilityProvider')
  return ctx
}
